import {
  type ButtonHTMLAttributes,
  cloneElement,
  type InputHTMLAttributes,
  isValidElement,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
  useEffect,
  useId,
  useRef,
  useState,
  useSyncExternalStore,
} from "react";
import { AlertIcon, ArrowDownIcon, ArrowUpIcon, ChevronRightIcon, CloseIcon, ErrorIcon, InboxIcon, InfoIcon, SuccessIcon } from "./icons.js";
import { type SortState, sortRows } from "./sort.js";

/**
 * Набор элементов дизайн-системы (docs/design/README.md). Без внешних UI-китов. Одна вёрстка на телефон и широкий
 * экран: раскладка меняется брейкпоинтами Tailwind (`lg:` от 1024px), а там, где на широком экране нужно
 * смонтировать другое дерево (две колонки вместо одной), компонент спрашивает `useWide()`.
 * Цвета, скругления и тени берутся из токенов `index.css` (`bg-brand`, `text-ink-2`, `rounded-card`, `shadow-pop`).
 *
 * Вертикальный ритм (docs/design/README.md): 4..8px между строками одной карточки, 12px между карточками списка
 * и полями формы, 16..24px между группами экрана (заголовок к содержимому, шапка к действиям, заголовок шторки),
 * 32px между разделами экрана (`SectionTitle`), 64px между секциями сайта.
 */

/** Широкий экран: тот же порог, что у `lg:` в Tailwind. */
export const WIDE_QUERY = "(min-width: 1024px)";
/** Ещё шире (`xl:`): хватает места на третью колонку. */
export const EXTRA_WIDE_QUERY = "(min-width: 1280px)";
/** Устройство с мышью и клавиатурой: Enter отправляет, подсказки про клавиши. */
export const POINTER_QUERY = "(hover: hover) and (pointer: fine)";

/** Функция подписки на каждый запрос одна: `useSyncExternalStore` переподписывается, если она меняется. */
const subscribers = new Map<string, (onChange: () => void) => () => void>();
const subscribeMedia = (query: string) => {
  let subscribe = subscribers.get(query);
  if (!subscribe) {
    subscribe = (onChange) => {
      if (typeof window === "undefined") return () => {};
      const mql = window.matchMedia(query);
      mql.addEventListener("change", onChange);
      return () => mql.removeEventListener("change", onChange);
    };
    subscribers.set(query, subscribe);
  }
  return subscribe;
};

/** Совпадает ли медиазапрос; обновляется при изменении размера окна. */
export function useMediaQuery(query: string): boolean {
  return useSyncExternalStore(
    subscribeMedia(query),
    () => window.matchMedia(query).matches,
    () => false,
  );
}

export const useWide = (): boolean => useMediaQuery(WIDE_QUERY);

export * from "./icons.js";
export { emptyLast, type SortState, sortRows, textComparator } from "./sort.js";

/** Видимый фокус с клавиатуры (мышь и касание его не показывают). */
export const focusRing = "outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2";

/** Текстовая ссылка в цвете бренда. */
export const linkClass = `rounded font-medium text-brand underline-offset-2 hover:underline ${focusRing}`;

/* Кнопки */

const buttonBase = `inline-flex items-center justify-center gap-2 rounded-field font-medium transition active:scale-[0.98] disabled:opacity-50 disabled:pointer-events-none ${focusRing}`;
const buttonSizes = {
  /** 36px: кнопки в шапке страницы, в карточках и таблицах. */
  sm: "px-3 py-2 text-sm",
  /** 44px: обычная кнопка. */
  md: "px-4 py-2.5 text-base",
  /** 48px: главное действие экрана на телефоне. */
  lg: "px-5 py-3 text-base",
};
export type ButtonSize = keyof typeof buttonSizes;
const buttonVariants = {
  primary: "bg-brand text-white hover:bg-brand-hover",
  secondary: "border border-line-strong bg-white text-ink hover:bg-slate-50",
  ghost: "text-ink-2 hover:bg-slate-100 hover:text-ink",
  /** Необратимое действие рядом с обычными (отменить запись): та же форма, красный текст. */
  danger: "bg-red-50 text-red-700 hover:bg-red-100",
  dangerGhost: "text-red-700 hover:bg-red-50",
};
export type ButtonVariant = keyof typeof buttonVariants;

/** Классы кнопки для ссылок, которые выглядят как кнопки (кнопку внутрь ссылки класть нельзя). */
export const buttonClass = (variant: ButtonVariant = "primary", className = "", size: ButtonSize = "md"): string =>
  `${buttonBase} ${buttonSizes[size]} ${buttonVariants[variant]} ${className}`;

/**
 * Кнопка. `icon` слева от текста, `loading` держит ширину и показывает спиннер вместо текста
 * (кнопка при этом недоступна, чтобы не отправить дважды).
 */
export function Button({
  className = "",
  variant = "primary",
  size = "md",
  icon,
  loading = false,
  disabled,
  children,
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: ButtonVariant; size?: ButtonSize; icon?: ReactNode; loading?: boolean }) {
  return (
    <button
      type="button"
      className={`${loading ? "relative" : ""} ${buttonClass(variant, className, size)}`}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      {...props}
    >
      {loading && (
        <span className="absolute inset-0 flex items-center justify-center" aria-hidden="true">
          <Spinner inline decorative />
        </span>
      )}
      <span className={`inline-flex items-center justify-center gap-2 ${loading ? "opacity-0" : ""}`}>
        {icon && (
          <span className="-ml-0.5 flex shrink-0 [&>svg]:h-5 [&>svg]:w-5" aria-hidden="true">
            {icon}
          </span>
        )}
        {children}
      </span>
    </button>
  );
}

/** Круглая кнопка-иконка с доступным именем (закрыть, назад, скопировать). */
export function IconButton({
  label,
  icon,
  variant = "ghost",
  size = "md",
  className = "",
  ...props
}: Omit<ButtonHTMLAttributes<HTMLButtonElement>, "children"> & { label: string; icon: ReactNode; variant?: "ghost" | "secondary"; size?: "sm" | "md" }) {
  const sizes = { sm: "h-9 w-9 [&>svg]:h-4 [&>svg]:w-4", md: "h-11 w-11 [&>svg]:h-5 [&>svg]:w-5" };
  const variants = {
    ghost: "text-ink-3 hover:bg-slate-100 hover:text-ink",
    secondary: "border border-line-strong bg-white text-ink-2 hover:bg-slate-50 hover:text-ink",
  };
  return (
    <button
      type="button"
      aria-label={label}
      className={`inline-flex shrink-0 items-center justify-center rounded-full transition disabled:opacity-50 disabled:pointer-events-none ${sizes[size]} ${variants[variant]} ${focusRing} ${className}`}
      {...props}
    >
      {icon}
    </button>
  );
}

/* Поля */

/**
 * Поле ввода. Фокус: рамка в цвете бренда и кольцо 1px вплотную к рамке (`ring-1` без отступа), поэтому выделение
 * повторяет скругление поля на любом браузере, в том числе в Chrome на Android. `aria-invalid` красит рамку в красный.
 */
const fieldBase =
  "w-full rounded-field border border-line-strong bg-white text-ink placeholder:text-ink-3 outline-none transition-[border-color,box-shadow] focus:border-brand focus:ring-1 focus:ring-brand aria-invalid:border-red-500 aria-invalid:focus:ring-red-500 disabled:bg-slate-50 disabled:text-ink-3";
const fieldClass = `${fieldBase} px-3.5 py-2.5 text-base`;
const fieldSizes = {
  md: "px-3.5 py-2.5 text-base",
  /** Крупное поле: код из письма, суммы. */
  lg: "px-4 py-3 text-2xl",
};
export type FieldSize = keyof typeof fieldSizes;

/** `fieldSize="lg"` делает поле крупным; размер задаётся пропом, а не классом снаружи: Tailwind 4 кладёт классы одного свойства по алфавиту, и `text-2xl` снаружи проиграл бы `text-base`. */
export function Input({ className = "", fieldSize = "md", ...props }: InputHTMLAttributes<HTMLInputElement> & { fieldSize?: FieldSize }) {
  return <input className={`${fieldBase} ${fieldSizes[fieldSize]} ${className}`} {...props} />;
}

export function Textarea({ className = "", ...props }: TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return <textarea className={`${fieldClass} ${className}`} {...props} />;
}

export function Select({ className = "", ...props }: SelectHTMLAttributes<HTMLSelectElement>) {
  return <select className={`${fieldClass} field-select ${className}`} {...props} />;
}

/** Поле с префиксом или суффиксом (`before`: иконка поиска, валюта; `after`: «мин»): рамка общая, фокус подсвечивает всю группу. */
export function InputGroup({ before, after, className = "", ...props }: InputHTMLAttributes<HTMLInputElement> & { before?: ReactNode; after?: ReactNode }) {
  return (
    <div
      className={`flex w-full items-center rounded-field border border-line-strong bg-white transition-[border-color,box-shadow] focus-within:border-brand focus-within:ring-1 focus-within:ring-brand ${className}`}
    >
      {before && <span className="shrink-0 pl-3.5 text-ink-3">{before}</span>}
      <input className="min-w-0 flex-1 bg-transparent px-3.5 py-2.5 text-base text-ink placeholder:text-ink-3 outline-none" {...props} />
      {after && <span className="shrink-0 pr-3.5 text-ink-3">{after}</span>}
    </div>
  );
}

export function Label({ children, htmlFor, className = "" }: { children: ReactNode; htmlFor?: string | undefined; className?: string }) {
  return (
    <label htmlFor={htmlFor} className={`mb-1.5 block text-sm font-medium text-ink ${className}`}>
      {children}
    </label>
  );
}

/** Подпись под полем: подсказка серым или ошибка красным. */
export function FieldHint({ error, children, className = "", id }: { error?: boolean; children: ReactNode; className?: string; id?: string | undefined }) {
  return (
    <p id={id} className={`mt-1.5 text-sm ${error ? "text-red-700" : "text-ink-3"} ${className}`}>
      {children}
    </p>
  );
}

/**
 * Связь подписи с элементом ввода для доступности: единственному дочернему элементу (`Input`, `Select`, `Textarea`)
 * добавляются `aria-invalid` при ошибке и `aria-describedby` на подсказку или ошибку (id `${htmlFor}-hint` и `${htmlFor}-error`).
 * Если детей несколько или `htmlFor` нет, атрибуты не ставятся: экран ставит их сам.
 */
function withFieldAria(children: ReactNode, htmlFor: string | undefined, hint: ReactNode, error: ReactNode): ReactNode {
  if (!htmlFor || !isValidElement<Record<string, unknown>>(children)) return children;
  const describedBy = error ? `${htmlFor}-error` : hint ? `${htmlFor}-hint` : undefined;
  return cloneElement(children, { "aria-invalid": error ? true : undefined, "aria-describedby": describedBy });
}

/** Поле формы в столбик: подпись, элемент ввода, подсказка или ошибка. Для подписи слева на широком экране есть `FormRow`. */
export function Field({
  label,
  htmlFor,
  hint,
  error,
  children,
}: {
  label: ReactNode;
  htmlFor?: string | undefined;
  hint?: ReactNode;
  error?: ReactNode;
  children: ReactNode;
}) {
  return (
    <div>
      <Label htmlFor={htmlFor}>{label}</Label>
      {withFieldAria(children, htmlFor, hint, error)}
      {error ? (
        <FieldHint error id={htmlFor ? `${htmlFor}-error` : undefined}>
          {error}
        </FieldHint>
      ) : hint ? (
        <FieldHint id={htmlFor ? `${htmlFor}-hint` : undefined}>{hint}</FieldHint>
      ) : null}
    </div>
  );
}

/**
 * Поле формы с подписью и подсказкой. В узком контейнере (телефон, шторка) подпись над полем;
 * в широком (от 32rem, контейнерный запрос `@lg`) подпись слева от поля, подсказка под полем.
 * Как `Field`, связывает элемент ввода с подсказкой и ошибкой (`aria-describedby`, `aria-invalid`).
 */
export function FormRow({
  label,
  htmlFor,
  hint,
  error,
  hintClass = "mt-1.5 text-sm text-ink-3",
  children,
}: {
  label: ReactNode;
  htmlFor?: string | undefined;
  hint?: ReactNode;
  error?: ReactNode;
  /** Классы подсказки, если на экране она была другого размера. */
  hintClass?: string;
  children: ReactNode;
}) {
  return (
    <div className="@container">
      <div className="@lg:grid @lg:grid-cols-[12rem_minmax(0,1fr)] @lg:items-start @lg:gap-x-6">
        <Label htmlFor={htmlFor} className="@lg:mb-0 @lg:pt-2.5">
          {label}
        </Label>
        <div className="min-w-0">
          {withFieldAria(children, htmlFor, hint, error)}
          {error ? (
            <FieldHint error id={htmlFor ? `${htmlFor}-error` : undefined}>
              {error}
            </FieldHint>
          ) : hint ? (
            <p className={hintClass} id={htmlFor ? `${htmlFor}-hint` : undefined}>
              {hint}
            </p>
          ) : null}
        </div>
      </div>
    </div>
  );
}

/** Выключатель: строка с подписью и тумблером справа. */
export function Toggle({
  checked,
  onChange,
  label,
  hint,
  disabled,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  label: ReactNode;
  hint?: ReactNode;
  disabled?: boolean;
}) {
  return (
    <label className="flex cursor-pointer items-center justify-between gap-4 py-2">
      <span>
        <span className="block">{label}</span>
        {hint && <span className="block text-sm text-ink-2">{hint}</span>}
      </span>
      <input type="checkbox" className="peer sr-only" checked={checked} disabled={disabled} onChange={(e) => onChange(e.target.checked)} />
      <span className="relative h-7 w-12 shrink-0 rounded-full bg-line-strong transition peer-checked:bg-brand peer-disabled:opacity-50 peer-focus-visible:ring-2 peer-focus-visible:ring-brand peer-focus-visible:ring-offset-2 after:absolute after:top-0.5 after:left-0.5 after:h-6 after:w-6 after:rounded-full after:bg-white after:shadow-sm after:transition peer-checked:after:translate-x-5" />
    </label>
  );
}

/* Поверхности и заголовки */

const cardTones = {
  default: "border-line bg-white shadow-card",
  /** Карточка в цвете бренда (главная цифра отчёта). */
  brand: "border-brand bg-brand text-white shadow-card",
  /** Тихая карточка на подложке страницы (пояснения, итог). */
  muted: "border-line bg-page",
  /** Строка, которую нужно проверить (черновик из источников). */
  warning: "border-amber-400 bg-amber-50/40 shadow-card",
};
export type CardTone = keyof typeof cardTones;

/**
 * Карточка. `selected` рисует её выбранной (рамка и подложка бренда), `tone` меняет фон. Фон и рамка задаются
 * пропами, а не классом снаружи: Tailwind 4 кладёт классы одного свойства по алфавиту, и `bg-brand` снаружи проиграл бы `bg-white`.
 */
export function Card({
  children,
  className = "",
  selected = false,
  tone = "default",
}: {
  children: ReactNode;
  className?: string;
  selected?: boolean;
  tone?: CardTone;
}) {
  const look = selected ? "border-brand bg-brand-soft ring-1 ring-brand" : cardTones[tone];
  return <div className={`rounded-card border p-4 lg:p-5 ${look} ${className}`}>{children}</div>;
}

/** Заголовок страницы; `actions` ставит кнопки справа на одной строке, `hint` подпись под заголовком. */
export function PageTitle({ children, actions, hint }: { children: ReactNode; actions?: ReactNode; hint?: ReactNode }) {
  const title = <h1 className="text-2xl font-semibold text-ink lg:text-3xl">{children}</h1>;
  if (!actions && !hint) return <div className="mb-5">{title}</div>;
  return (
    <div className="mb-5">
      <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-3">
        {title}
        {actions && <div className="flex shrink-0 items-center gap-2">{actions}</div>}
      </div>
      {hint && <p className="mt-1 text-sm text-ink-2">{hint}</p>}
    </div>
  );
}

/**
 * Подзаголовок секции: 32px сверху от предыдущего блока. `flush` убирает верхний отступ (первый раздел сразу под
 * заголовком страницы); для верхней строки колонок сетки на компьютере достаточно `className="lg:mt-0"`.
 * Сам `mt-0` снаружи не сработал бы: Tailwind 4 кладёт классы одного свойства по алфавиту, и `mt-8` победил бы.
 */
export function SectionTitle({ children, className = "", flush = false }: { children: ReactNode; className?: string; flush?: boolean }) {
  return <h2 className={`${flush ? "" : "mt-8"} mb-3 text-xs font-semibold uppercase tracking-wider text-ink-3 ${className}`}>{children}</h2>;
}

/** Экран без оболочки кабинета (вход, приглашение): на телефоне во всю ширину, от `sm` карточка по центру. */
export function Screen({ children }: { children: ReactNode }) {
  return (
    <div className="flex min-h-dvh w-full items-start justify-center px-4 py-6 sm:py-12">
      <div className="w-full max-w-md sm:rounded-sheet sm:border sm:border-line sm:bg-white sm:p-8 sm:shadow-card">{children}</div>
    </div>
  );
}

/* Уведомления и состояния */

const noticeStyles = {
  info: { box: "bg-brand-soft text-brand-text", Icon: InfoIcon },
  error: { box: "bg-red-50 text-red-800", Icon: ErrorIcon },
  success: { box: "bg-emerald-50 text-emerald-800", Icon: SuccessIcon },
  warning: { box: "bg-amber-50 text-amber-800", Icon: AlertIcon },
};
export type NoticeKind = keyof typeof noticeStyles;

/** Плашка с сообщением; `action` (кнопка «Повторить», ссылка) справа или снизу на узком экране. */
export function Notice({ children, kind = "info", action, icon = true }: { children: ReactNode; kind?: NoticeKind; action?: ReactNode; icon?: boolean }) {
  const { box, Icon } = noticeStyles[kind];
  return (
    <div role={kind === "error" ? "alert" : undefined} className={`flex flex-wrap items-start gap-x-3 gap-y-2 rounded-field px-4 py-3 text-sm ${box}`}>
      {icon && <Icon className="mt-0.5 h-5 w-5 shrink-0" />}
      <div className="min-w-0 flex-1 basis-40">{children}</div>
      {action && <div className="shrink-0">{action}</div>}
    </div>
  );
}

/** Индикатор ожидания; `inline` без внешних отступов (внутри кнопки или строки). */
export function Spinner({ inline = false, decorative = false, className = "" }: { inline?: boolean; decorative?: boolean; className?: string }) {
  const cls = `${inline ? "h-5 w-5 border-2" : "mx-auto my-8 h-6 w-6 border-2"} animate-spin rounded-full border-line-strong border-t-brand ${className}`;
  // Декоративный спиннер (внутри кнопки, у которой есть свой текст) не объявляется статусом: имя кнопки остаётся её текстом.
  if (decorative) return <div className={cls} aria-hidden="true" />;
  return <div className={cls} role="status" aria-label="loading" />;
}

/** Полоса-заглушка на время загрузки (ширина и высота классами). */
export function Skeleton({ className = "" }: { className?: string }) {
  return <div className={`skeleton rounded-md bg-slate-200/80 ${className}`} aria-hidden="true" />;
}

/** Скелетон списка карточек вместо голого спиннера на экранах со списками. */
export function SkeletonList({ rows = 3, className = "" }: { rows?: number; className?: string }) {
  return (
    <div className={`space-y-3 ${className}`} role="status" aria-label="loading">
      {Array.from({ length: rows }, (_, i) => `row-${i}`).map((key) => (
        <div key={key} className="rounded-card border border-line bg-white p-4">
          <div className="flex items-center justify-between gap-3">
            <Skeleton className="h-4 w-1/3" />
            <Skeleton className="h-5 w-20 rounded-full" />
          </div>
          <Skeleton className="mt-3 h-4 w-1/2" />
          <Skeleton className="mt-2 h-3 w-2/3" />
        </div>
      ))}
    </div>
  );
}

/** Пустое состояние: иконка в круге, заголовок, строка пояснения и при необходимости одно действие. */
export function EmptyState({
  icon,
  title,
  text,
  action,
  className = "",
}: {
  icon?: ReactNode;
  title: ReactNode;
  text?: ReactNode;
  action?: ReactNode;
  className?: string;
}) {
  return (
    <div className={`flex flex-col items-center px-4 py-12 text-center ${className}`}>
      <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-slate-100 text-ink-3 [&>svg]:h-6 [&>svg]:w-6">
        {icon ?? <InboxIcon />}
      </div>
      <div className="font-semibold text-ink">{title}</div>
      {text && <p className="mt-1 max-w-xs text-sm text-ink-2">{text}</p>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

const tones = {
  slate: "bg-slate-100 text-slate-700",
  amber: "bg-amber-50 text-amber-800",
  green: "bg-emerald-50 text-emerald-800",
  blue: "bg-sky-50 text-sky-800",
  red: "bg-red-50 text-red-700",
  brand: "bg-brand-soft text-brand-text",
} as const;
const toneDots = {
  slate: "bg-slate-400",
  amber: "bg-amber-500",
  green: "bg-emerald-500",
  blue: "bg-sky-500",
  red: "bg-red-500",
  brand: "bg-brand",
} as const;
export type Tone = keyof typeof tones;

/** Бейдж статуса; `dot` добавляет цветную точку слева (статусы записи и диалога). */
export function Badge({ children, tone = "slate", dot = false, className = "" }: { children: ReactNode; tone?: Tone; dot?: boolean; className?: string }) {
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-medium ${tones[tone]} ${className}`}>
      {dot && <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${toneDots[tone]}`} aria-hidden="true" />}
      {children}
    </span>
  );
}

/** Кружок с инициалами (клиент в списке диалогов, мастер). */
export function Avatar({ name, size = "md", className = "" }: { name: string; size?: "sm" | "md" | "lg"; className?: string }) {
  const initials =
    name
      .replace(/^@/, "")
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((w) => w[0]?.toUpperCase() ?? "")
      .join("") || "?";
  const sizes = { sm: "h-8 w-8 text-xs", md: "h-10 w-10 text-sm", lg: "h-14 w-14 text-lg" };
  return (
    <span
      className={`inline-flex shrink-0 items-center justify-center rounded-full bg-brand-soft font-semibold text-brand-text ${sizes[size]} ${className}`}
      aria-hidden="true"
    >
      {initials}
    </span>
  );
}

/** Кнопка-чип для выбора (услуги, мастер, время). */
export function Chip({
  active,
  children,
  onClick,
  disabled,
  className = "",
}: {
  active?: boolean;
  children: ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  className?: string;
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      aria-pressed={active}
      onClick={onClick}
      className={`shrink-0 rounded-full border px-4 py-2.5 text-sm font-medium transition disabled:opacity-40 ${focusRing} ${
        active ? "border-brand bg-brand text-white" : "border-line-strong bg-white text-ink hover:bg-slate-50"
      } ${className}`}
    >
      {children}
    </button>
  );
}

/**
 * Переключатель-сегменты (фильтры «Ждут / Мастер / Ассистент / Все»): один выбранный, остальные тихие.
 * Группа получает доступное имя из `label`; у каждого сегмента `aria-pressed`.
 */
export function Segmented<V extends string>({
  options,
  value,
  onChange,
  label,
  className = "",
}: {
  options: { value: V; label: ReactNode; badge?: number }[];
  value: V;
  onChange: (v: V) => void;
  label: string;
  className?: string;
}) {
  return (
    <fieldset aria-label={label} className={`no-scrollbar flex max-w-full gap-1 overflow-x-auto rounded-field bg-slate-100 p-1 ${className}`}>
      {options.map((o) => {
        const active = o.value === value;
        return (
          <button
            key={o.value}
            type="button"
            aria-pressed={active}
            onClick={() => onChange(o.value)}
            className={`flex shrink-0 items-center gap-1.5 rounded-[9px] px-3 py-1.5 text-sm font-medium transition ${focusRing} ${
              active ? "bg-white text-ink shadow-card" : "text-ink-2 hover:text-ink"
            }`}
          >
            {o.label}
            {o.badge != null && o.badge > 0 && (
              <span className={`rounded-full px-1.5 text-xs font-semibold ${active ? "bg-brand-soft text-brand-text" : "bg-slate-200 text-ink-2"}`}>
                {o.badge}
              </span>
            )}
          </button>
        );
      })}
    </fieldset>
  );
}

/**
 * Шторка поверх экрана (услуга, запись, отгул). На телефоне снизу, на широком окне по центру.
 * `side`: на широком экране не окно, а боковая панель справа (детали записи): страница под ней остаётся
 * видимой и кликабельной, панель закрывается крестиком и Esc.
 * Закрывается фоном, крестиком и Esc. Высота до 90% экрана, содержимое прокручивается.
 */
export function Sheet({
  open,
  onClose,
  title,
  children,
  side = false,
}: {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  children: ReactNode;
  side?: boolean;
}) {
  const titleId = useId();
  const dialogRef = useRef<HTMLDivElement>(null);
  // `onClose` почти всегда новая стрелка на каждый рендер родителя: держим её в ref, чтобы эффект открытия
  // (фокус внутрь, блокировка прокрутки) не перезапускался и не уводил фокус из поля при каждой перерисовке.
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  });
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onCloseRef.current();
    window.addEventListener("keydown", onKey);
    // Боковая панель на широком экране страницу не перекрывает, прокрутку не трогаем.
    const lock = !(side && window.matchMedia(WIDE_QUERY).matches);
    const body = document.body.style;
    const prev = { overflow: body.overflow, paddingRight: body.paddingRight };
    if (lock) {
      // Полоса прокрутки пропадает вместе с прокруткой, и страница под шторкой сдвигается вправо на её ширину:
      // возвращаем эту ширину отступом справа (к отступу выреза экрана из index.css).
      const gap = window.innerWidth - document.documentElement.clientWidth;
      body.overflow = "hidden";
      if (gap > 0) body.paddingRight = `calc(env(safe-area-inset-right) + ${gap}px)`;
    }
    // Фокус внутрь шторки при открытии и обратно на то, что было в фокусе, при закрытии.
    const before = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    dialogRef.current?.focus();
    return () => {
      window.removeEventListener("keydown", onKey);
      if (lock) {
        body.overflow = prev.overflow;
        body.paddingRight = prev.paddingRight;
      }
      before?.focus();
    };
  }, [open, side]);
  if (!open) return null;
  return (
    <div
      className={`fixed inset-0 z-40 flex items-end justify-center sm:items-center sm:p-4 ${side ? "lg:pointer-events-none lg:items-stretch lg:justify-end lg:p-0" : ""}`}
    >
      <button type="button" aria-label="close" className={`absolute inset-0 bg-ink/40 ${side ? "lg:hidden" : ""}`} onClick={onClose} />
      <div
        ref={dialogRef}
        role="dialog"
        tabIndex={-1}
        aria-modal={side ? undefined : "true"}
        aria-labelledby={titleId}
        className={`relative z-10 flex max-h-[90dvh] w-full max-w-md flex-col rounded-t-sheet bg-white pb-[env(safe-area-inset-bottom)] shadow-pop outline-none sm:rounded-sheet sm:pb-0 ${
          side ? "lg:pointer-events-auto lg:max-h-none lg:w-[26rem] lg:rounded-none lg:border-l lg:border-line lg:shadow-none" : ""
        }`}
      >
        {/* Ручка шторки на телефоне: подсказывает, что её можно закрыть жестом вниз (само закрытие по крестику). */}
        <div className="mx-auto mt-2 h-1 w-10 rounded-full bg-line-strong sm:hidden" aria-hidden="true" />
        <div className="flex items-center justify-between gap-3 px-4 pt-3 pb-3 sm:pt-4">
          <div id={titleId} className="text-lg font-semibold text-ink">
            {title}
          </div>
          <IconButton label="close" icon={<CloseIcon />} onClick={onClose} className="-mr-2" />
        </div>
        <div className="overflow-y-auto px-4 pt-1 pb-6">{children}</div>
      </div>
    </div>
  );
}

/** Строка списка-меню: заголовок, подпись, стрелка; ведёт по ссылке или вызывает действие. */
export function MenuRow({
  title,
  hint,
  right,
  icon,
  onClick,
}: {
  title: ReactNode;
  hint?: ReactNode;
  right?: ReactNode;
  icon?: ReactNode;
  onClick?: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`-mx-4 flex w-[calc(100%+2rem)] items-center gap-3 border-b border-line px-4 py-3 text-left transition first:rounded-t-card last:rounded-b-card last:border-b-0 hover:bg-slate-50 focus-visible:rounded-md ${focusRing}`}
    >
      {icon && <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-slate-100 text-ink-2 [&>svg]:h-5 [&>svg]:w-5">{icon}</span>}
      <span className="min-w-0 flex-1">
        <span className="block text-ink">{title}</span>
        {hint && <span className="block truncate text-sm text-ink-2">{hint}</span>}
      </span>
      <span className="flex shrink-0 items-center gap-2 text-ink-3">
        {right}
        <ChevronRightIcon className="h-5 w-5" />
      </span>
    </button>
  );
}

export interface Column<T> {
  key: string;
  title: ReactNode;
  render: (row: T) => ReactNode;
  /** Сравнение для сортировки по столбцу; без него столбец не сортируется. */
  sort?: (a: T, b: T) => number;
  /** Сравнение, которое само знает направление (пустые значения в конец при любом направлении). */
  sortWithDir?: (dir: "asc" | "desc") => (a: T, b: T) => number;
  className?: string;
}

/**
 * Таблица с сортировкой по столбцам для широкого экрана (клиенты, визиты). Заголовок сортируемого столбца
 * это кнопка: первый клик по возрастанию, второй по убыванию. Строка кликабельна, выбранная подсвечена.
 */
export function DataTable<T>({
  label,
  columns,
  rows,
  rowKey,
  onRowClick,
  selectedKey,
  defaultSort,
  rowLabel,
}: {
  /** Доступное имя таблицы. */
  label: string;
  columns: Column<T>[];
  rows: T[];
  rowKey: (row: T) => string;
  onRowClick?: (row: T) => void;
  selectedKey?: string | null;
  defaultSort?: SortState;
  /** Доступное имя строки (кнопка в первой ячейке). */
  rowLabel?: (row: T) => string;
}) {
  const [sort, setSort] = useState<SortState | null>(defaultSort ?? null);
  const sorted = sortRows(rows, columns, sort);
  const toggle = (key: string) => setSort((cur) => (cur?.key === key ? { key, dir: cur.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" }));
  return (
    <div className="overflow-x-auto rounded-card border border-line bg-white shadow-card">
      <table className="w-full text-left text-sm" aria-label={label}>
        <thead className="bg-page text-xs uppercase tracking-wider text-ink-3">
          <tr className="border-b border-line">
            {columns.map((c) => (
              <th
                key={c.key}
                scope="col"
                className={`px-4 py-2.5 font-semibold ${c.className ?? ""}`}
                aria-sort={sort?.key === c.key ? (sort.dir === "asc" ? "ascending" : "descending") : undefined}
              >
                {c.sort || c.sortWithDir ? (
                  <button
                    type="button"
                    onClick={() => toggle(c.key)}
                    className={`inline-flex items-center gap-1 rounded uppercase hover:text-ink ${focusRing}`}
                  >
                    {c.title}
                    <span aria-hidden="true" className={sort?.key === c.key ? "text-brand" : "opacity-30"}>
                      {sort?.key === c.key && sort.dir === "desc" ? <ArrowDownIcon className="h-3.5 w-3.5" /> : <ArrowUpIcon className="h-3.5 w-3.5" />}
                    </span>
                  </button>
                ) : (
                  c.title
                )}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sorted.map((row) => {
            const key = rowKey(row);
            const selected = key === selectedKey;
            // Мышью кликабельна вся строка; с клавиатуры строку открывает кнопка в первой ячейке (у `tr` нет роли кнопки,
            // а кольцо фокуса на `tr` в Safari не рисуется). Выбранная строка в цвете бренда плюс полоса слева.
            return (
              <tr
                key={key}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
                className={`border-b border-line last:border-b-0 ${onRowClick ? "cursor-pointer hover:bg-slate-50" : ""} ${
                  selected ? "bg-brand-soft shadow-[inset_3px_0_0_var(--color-brand)] hover:bg-brand-soft" : ""
                }`}
              >
                {columns.map((c, i) => (
                  <td key={c.key} className={`px-4 py-2.5 align-top ${c.className ?? ""}`}>
                    {i === 0 && onRowClick ? (
                      <button
                        type="button"
                        aria-current={selected ? "true" : undefined}
                        aria-label={rowLabel?.(row)}
                        onClick={(e) => {
                          e.stopPropagation();
                          onRowClick(row);
                        }}
                        className={`rounded text-left ${focusRing}`}
                      >
                        {c.render(row)}
                      </button>
                    ) : (
                      c.render(row)
                    )}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
